import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash, PushPin, PushPinSlash, MagnifyingGlass, Spinner,
  Note as StickyNote, X, FloppyDisk
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = [
  { id: 'yellow',  bg: 'bg-amber-100 dark:bg-amber-500/15',     border: 'border-amber-300/60 dark:border-amber-500/30',     dot: 'bg-amber-400' },
  { id: 'rose',    bg: 'bg-rose-100 dark:bg-rose-500/15',       border: 'border-rose-300/60 dark:border-rose-500/30',       dot: 'bg-rose-400' },
  { id: 'sky',     bg: 'bg-sky-100 dark:bg-sky-500/15',         border: 'border-sky-300/60 dark:border-sky-500/30',         dot: 'bg-sky-400' },
  { id: 'green',   bg: 'bg-emerald-100 dark:bg-emerald-500/15', border: 'border-emerald-300/60 dark:border-emerald-500/30', dot: 'bg-emerald-400' },
  { id: 'violet',  bg: 'bg-violet-100 dark:bg-violet-500/15',   border: 'border-violet-300/60 dark:border-violet-500/30',   dot: 'bg-violet-400' },
  { id: 'pink',    bg: 'bg-pink-100 dark:bg-pink-500/15',       border: 'border-pink-300/60 dark:border-pink-500/30',       dot: 'bg-pink-400' },
];
const colorOf = (c) => COLORS.find((x) => x.id === c) || COLORS[0];

export default function NotesPage() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // { id, title, content, color, pinned } | null
  const [search, setSearch] = useState('');

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/notes`);
      setNotes(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const filtered = useMemo(() => {
    if (!search.trim()) return notes;
    const q = search.toLowerCase();
    return notes.filter(n =>
      (n.title || '').toLowerCase().includes(q) ||
      (n.content || '').toLowerCase().includes(q)
    );
  }, [notes, search]);

  const pinnedNotes = filtered.filter(n => n.pinned);
  const otherNotes  = filtered.filter(n => !n.pinned);

  const startNew = () => setEditing({ id: null, title: '', content: '', color: 'yellow', pinned: false });

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.content.trim() && !editing.title.trim()) {
      setEditing(null);
      return;
    }
    try {
      const payload = {
        title: editing.title || '',
        content: editing.content || '',
        color: editing.color || 'yellow',
        pinned: !!editing.pinned,
      };
      if (editing.id) {
        await axios.put(`${API_URL}/notes/${editing.id}`, payload);
        toast.success('Note updated');
      } else {
        await axios.post(`${API_URL}/notes`, payload);
        toast.success('Note saved');
      }
      setEditing(null);
      fetchNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save note');
    }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API_URL}/notes/${id}`);
      setNotes(prev => prev.filter(n => n.id !== id));
      toast.success('Note deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete note');
    }
  };

  const togglePin = async (note, e) => {
    e?.stopPropagation();
    try {
      await axios.put(`${API_URL}/notes/${note.id}`, { pinned: !note.pinned });
      fetchNotes();
    } catch (err) {
      toast.error('Failed to pin/unpin');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Spinner size={28} className="animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="notes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <StickyNote size={24} weight="fill" className="text-[#d4a84b]" />
            My Notes
          </h1>
          <p className="text-muted-foreground text-sm">A personal scratchpad — phone numbers, calculations, anything.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search notes…"
              className="pl-8 w-56"
              data-testid="notes-search"
            />
          </div>
          <Button onClick={startNew} data-testid="notes-new-btn"
            className="bg-[#d4a84b] hover:bg-[#e8bc5e] text-[#0a0a0a]">
            <Plus size={16} className="mr-1.5" /> New Note
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="text-center py-20 border-2 border-dashed rounded-2xl">
          <StickyNote size={42} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground mb-4">
            {search ? 'No notes match your search.' : 'Nothing here yet — capture a quick thought.'}
          </p>
          {!search && <Button onClick={startNew} variant="outline" size="sm"><Plus size={14} className="mr-1.5" /> Create your first note</Button>}
        </div>
      )}

      {/* Pinned section */}
      {pinnedNotes.length > 0 && (
        <div data-testid="notes-pinned">
          <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <PushPin size={12} weight="fill" /> Pinned
          </div>
          <NoteGrid notes={pinnedNotes} onEdit={setEditing} onDelete={handleDelete} onTogglePin={togglePin} />
        </div>
      )}

      {/* Others */}
      {otherNotes.length > 0 && (
        <div data-testid="notes-others">
          {pinnedNotes.length > 0 && (
            <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Others
            </div>
          )}
          <NoteGrid notes={otherNotes} onEdit={setEditing} onDelete={handleDelete} onTogglePin={togglePin} />
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setEditing(null)} data-testid="note-editor-backdrop">
          <div onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-lg rounded-2xl border ${colorOf(editing.color).border} ${colorOf(editing.color).bg} shadow-2xl backdrop-blur`}>
            <div className="p-4 border-b border-current/10 flex items-center gap-2">
              <Input
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="Title (optional)"
                className="border-0 bg-transparent px-0 text-base font-semibold focus-visible:ring-0"
                data-testid="note-title-input"
              />
              <Button variant="ghost" size="sm" onClick={() => setEditing({ ...editing, pinned: !editing.pinned })} title={editing.pinned ? 'Unpin' : 'Pin'} data-testid="note-pin-toggle">
                {editing.pinned ? <PushPinSlash size={16} /> : <PushPin size={16} />}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(null)} data-testid="note-close-btn">
                <X size={16} />
              </Button>
            </div>
            <Textarea
              value={editing.content}
              onChange={(e) => setEditing({ ...editing, content: e.target.value })}
              placeholder="Type your note… (e.g. phone number, formula, snippet)"
              rows={8}
              autoFocus
              className="border-0 bg-transparent rounded-none focus-visible:ring-0 resize-none font-mono text-sm"
              data-testid="note-content-input"
            />
            <div className="flex items-center justify-between p-3 border-t border-current/10">
              <div className="flex items-center gap-1.5">
                {COLORS.map(c => (
                  <button key={c.id} type="button" onClick={() => setEditing({ ...editing, color: c.id })}
                    className={`w-6 h-6 rounded-full ${c.dot} ${editing.color === c.id ? 'ring-2 ring-offset-2 ring-offset-background ring-foreground/40' : ''}`}
                    aria-label={`color-${c.id}`}
                    data-testid={`note-color-${c.id}`}
                  />
                ))}
              </div>
              <Button onClick={handleSave} className="bg-[#0f172a] dark:bg-[#d4a84b] hover:bg-[#1e293b] dark:hover:bg-[#e8bc5e] text-white dark:text-[#0a0a0a]" data-testid="note-save-btn">
                <FloppyDisk size={14} className="mr-1.5" /> Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NoteGrid({ notes, onEdit, onDelete, onTogglePin }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="note-grid">
      {notes.map((n) => {
        const c = colorOf(n.color);
        return (
          <div
            key={n.id}
            onClick={() => onEdit({ ...n })}
            className={`group cursor-pointer rounded-xl border ${c.border} ${c.bg} p-4 hover:-translate-y-0.5 hover:shadow-lg transition-all min-h-[140px] flex flex-col`}
            data-testid={`note-card-${n.id}`}
          >
            <div className="flex items-start justify-between mb-1">
              <h3 className="font-semibold text-sm truncate flex-1">{n.title || 'Untitled'}</h3>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={(e) => onTogglePin(n, e)} className="p-1 rounded hover:bg-foreground/10" title={n.pinned ? 'Unpin' : 'Pin'} data-testid={`note-pin-${n.id}`}>
                  {n.pinned ? <PushPinSlash size={12} /> : <PushPin size={12} />}
                </button>
                <button onClick={(e) => onDelete(n.id, e)} className="p-1 rounded hover:bg-red-500/15 text-red-500" title="Delete" data-testid={`note-delete-${n.id}`}>
                  <Trash size={12} />
                </button>
              </div>
            </div>
            <p className="text-xs text-foreground/70 whitespace-pre-wrap line-clamp-6 flex-1 font-mono">{n.content || '—'}</p>
            <div className="text-[10px] text-foreground/50 mt-2 flex items-center gap-1.5">
              <span>{new Date(n.updated_at).toLocaleString()}</span>
              {n.pinned && <Badge variant="outline" className="text-[9px] py-0 px-1.5 h-4">PINNED</Badge>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
